import { difference, sortBy } from "lodash";
import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { MealKind, MealPlanAttributes } from "@/gql/graphql";
import { newTimeOfDay } from "@/utils/datetime";

import MealPlanFormStore from "./MealPlanFormStore";
import { PlannedMeal } from "./PlannedMeal";

export default class Rows {
  // Store already created PlannedMeal instances here so that there's only one
  // PlannedMeal instance lying around for each uuid.
  private instances: Map<string, PlannedMeal> = new Map();
  private store: MealPlanFormStore;

  get availableMealTypes(): MealKind[] {
    return difference(Object.values(MealKind), this.existingMealTypes);
  }

  get existingMealTypes(): MealKind[] {
    return this.array.map((pm) => pm.meal);
  }

  get existingTimes(): Date[] {
    return this.array.map((pm) => pm.recommendedTime);
  }

  private get array() {
    return sortBy(Array.from(this.instances.values()), "recommendedTime");
  }

  constructor(store: MealPlanFormStore) {
    makeAutoObservable(this);
    this.store = store;
    // Add default planned meals.
    this.add(MealKind.Breakfast, newTimeOfDay(7));
    this.add(MealKind.Lunch, newTimeOfDay(12));
    this.add(MealKind.Dinner, newTimeOfDay(19));
  }

  clear(): void {
    this.instances.clear();
  }

  has(plannedMeal: PlannedMeal): boolean {
    return this.instances.has(plannedMeal.uuid);
  }

  add(meal: MealKind, recommendedTime: Date, uuid = uuidv4()): PlannedMeal {
    const existing = this.instances.get(uuid);
    if (existing) {
      existing.meal = meal;
      existing.recommendedTime = recommendedTime;
      return existing;
    } else {
      this.instances.set(
        uuid,
        new PlannedMeal(this.store, meal, recommendedTime, uuid)
      );
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.instances.get(uuid)!;
    }
  }

  at(index: number): PlannedMeal {
    return this.array[index];
  }

  remove(plannedMeal: PlannedMeal): void {
    this.store.handleRemovedPlannedMealItems(plannedMeal.plannedMealItems);
    this.instances.delete(plannedMeal.uuid);
  }

  map<T>(fn: (plannedMeal: PlannedMeal) => T): T[] {
    return this.array.map(fn);
  }

  flatMap<T>(fn: (plannedMeal: PlannedMeal) => T | ReadonlyArray<T>): T[] {
    return this.array.flatMap(fn);
  }

  filter(fn: (plannedMeal: PlannedMeal) => boolean): PlannedMeal[] {
    return this.array.filter(fn);
  }

  previous(plannedMeal: PlannedMeal): PlannedMeal {
    return this.at(Math.max(this.array.indexOf(plannedMeal) - 1, 0));
  }

  next(plannedMeal: PlannedMeal): PlannedMeal {
    return this.at(
      Math.min(this.array.indexOf(plannedMeal) + 1, this.instances.size - 1)
    );
  }

  toAttributes(): MealPlanAttributes["syncPlannedMealsAttributes"] {
    return this.array.map((pm) => pm.toAttributes());
  }
}
